import React, { Fragment, useEffect, useState } from 'react';
import {
  Body,
  Box,
  Container,
  Heading,
  Icon,
  Select,
  Divider,
} from '@hover/blueprint';
import { iArrowRight } from '@hover/icons';
import { Layout } from '../../v2/components/common';
import { INewsItem, TNewPageStates } from '../../v2/interfaces/INewsItem';
import {
  NEWS_PAGE_STATES,
  PRESS_COVERAGE,
  PRESS_RELEASES,
} from '../../v2/constants';
import {
  BackToTopButton,
  DownloadButton,
  Footer,
  NewsItem,
  Sidebar,
  YearSelector,
} from '../../v2/components/news';

const Press = () => {
  const refactoredData = (arr: INewsItem[]) => {
    if (arr.length) {
      return arr.reduce<{ [key: string]: INewsItem[] }>((acc, curr) => {
        const y = curr.date.split(', ')[1];
        if (!acc[y]) acc[y] = [curr];
        else acc[y] = [...acc[y], curr];
        return acc;
      }, {});
    }
    return {};
  };

  const desktopDisableStates: TNewPageStates[] = ['Media Kit'];
  const pressReleaseData = refactoredData(PRESS_RELEASES);
  const pressCoverageData = refactoredData(PRESS_COVERAGE);

  const [state, setState] = useState<TNewPageStates>('Press Releases');
  const [showButton, setShowButton] = useState<boolean>(false);
  const [showDivider, setShowDivider] = useState<boolean>(true);
  const [selectedData, setSelectedData] = useState(pressReleaseData);
  const [canSeeMore, setCanSeeMore] = useState<boolean>(false);
  const [options, setOptions] = useState(
    Object.keys(pressReleaseData).reverse(),
  );
  const [year, setYear] = useState(Object.keys(pressReleaseData).reverse()[0]);

  useEffect(() => {
    const shouldShowDivider = () => window.innerWidth > parseInt('834px', 10);

    window.addEventListener('resize', () => {
      setShowDivider(shouldShowDivider());
    });

    setShowDivider(shouldShowDivider());
    return () =>
      window.removeEventListener('resize', () => {
        setShowDivider(shouldShowDivider());
      });
  }, []);

  useEffect(() => {
    setSelectedData(
      state === 'Press Coverage' ? pressCoverageData : pressReleaseData,
    );
    setOptions(Object.keys(selectedData).reverse());
    setYear(options[0]);
    setShowButton(true);
  }, []);

  const handleClickYear = (item: string) => {
    setYear(item);
    setCanSeeMore(false);
  };

  const handleClickSidebar = (item: TNewPageStates, isMobileMode?: boolean) => {
    if (isMobileMode) {
      setState(item);
      if (window) {
        window.scroll({
          top: 0,
          left: 0,
        });
      }
    }

    if (!desktopDisableStates.includes(item)) {
      if (!isMobileMode) {
        setState(item);
      }
      const selectedDataTemp =
        item === 'Press Coverage' ? pressCoverageData : pressReleaseData;
      const optionsTemp = Object.keys(selectedDataTemp).reverse();

      setSelectedData(selectedDataTemp);
      handleClickYear(optionsTemp[0]);
      setOptions(optionsTemp);
    }
  };

  const minimumDataLength = () => (state === 'Press Coverage' ? 10 : 6);

  const useValue = () => {
    const min = minimumDataLength();
    const years = year;
    const canSee = canSeeMore;
    return { min, years, canSee };
  };

  useValue();

  return (
    <Layout
      title="News"
      description="We share the latest industry trends, best practices, HOVER product and company updates with exterior contractors and insurance carriers here."
      showDivider={showDivider}
    >
      <Container
        px={500}
        maxW="1232px"
        overflow="visible"
        bg="neutral.0"
        zIndex={{ base: 10, desktop: 1 }}
        position={{ base: 'sticky', tablet: 'static' }}
        top={900}
        shadow={{
          base: showButton ? 'distance300' : 'none',
          tablet: 'none',
        }}
      >
        <Box
          alignItems="start"
          display="flex"
          flexDir="column"
          pb={{ base: '54px', desktop: 0 }}
        >
          <Heading
            py={{ base: 600, tablet: 800 }}
            fontWeight={{ base: 600, tablet: 700 }}
            size={{ base: 500, tablet: 800 }}
            textTransform="capitalize"
            style={{ marginBottom: 0 }}
          >
            Press
          </Heading>
          <Select
            d={{ base: 'block', tablet: 'none' }}
            onChange={({ target: { value } }) =>
              handleClickSidebar(value as TNewPageStates, true)
            }
            value={state}
          >
            {Object.values(NEWS_PAGE_STATES).map(
              (pageState: TNewPageStates) => (
                <option value={pageState} key={pageState}>
                  {pageState}
                </option>
              ),
            )}
          </Select>
        </Box>
      </Container>
      <Container
        maxW="1232px"
        overflow="unset!important"
        alignItems="center"
        display="flex"
        px={500}
        pb={{ base: '45px', tablet: 1100 }}
      >
        <Box
          display="flex"
          flexDirection={{ base: 'column', tablet: 'row' }}
          w="100%"
          overflow="unset!important"
        >
          <Box
            display={{ base: 'none', tablet: 'flex' }}
            mr="135px"
            overflow="unset!important"
          >
            <Box
              position="sticky"
              top={{ tablet: '65px', desktop: '96px' }}
              width="276px"
              height={{ tablet: '436px', desktop: '376px' }}
            >
              <Sidebar onClick={handleClickSidebar} state={state} />
            </Box>
          </Box>
          <Box display="flex" flexDirection="column">
            {state !== 'Media Kit' && (
              <YearSelector
                selectedValue={year}
                onClick={handleClickYear}
                options={options}
              />
            )}
            <Box
              mt={700}
              flexDir="column"
              d={{
                base: desktopDisableStates.includes(state) ? 'none' : 'flex',
                desktop: 'flex',
              }}
            >
              {selectedData[year]
                ?.slice(
                  0,
                  canSeeMore
                    ? selectedData[year].length - 1
                    : minimumDataLength(),
                )
                .map((item, index) => (
                  <Fragment key={item.title}>
                    <NewsItem item={item} />
                    <Divider
                      color="neutral.200"
                      opacity={1}
                      mt={700}
                      mb={selectedData[year].length - 1 === index ? 0 : 700}
                    />
                  </Fragment>
                ))}
            </Box>
            <Box
              d={{
                base: desktopDisableStates.includes(state) ? 'flex' : 'none',
                desktop: 'none',
              }}
              mb={1000}
            >
              <DownloadButton
                description={
                  state === 'Media Kit'
                    ? 'Our fact sheet is available for download.'
                    : 'Logos and brand guidelines are available for download.'
                }
                downloadLink="https://drive.google.com/drive/folders/1-zxRmGMRVLADvgOLfyekJX08ELhqy5Q5"
              />
            </Box>
            {selectedData[year]?.length > minimumDataLength() ? (
              <Box
                as="button"
                display={{
                  base: desktopDisableStates.includes(state) ? 'none' : 'flex',
                  desktop: 'flex',
                }}
                alignItems="center"
                onClick={() => {
                  setCanSeeMore(!canSeeMore);
                }}
              >
                <Body color="primary.600" size={500} isSemiBold>
                  {canSeeMore ? 'See less' : 'See more'}
                </Body>
                <Icon icon={iArrowRight} color="primary.600" size="medium" />
              </Box>
            ) : null}
          </Box>
        </Box>
      </Container>
      <Footer />
      <BackToTopButton
        distance={10}
        showButton={showButton}
        setShowButton={setShowButton}
      />
    </Layout>
  );
};

export default Press;
